import type { ReactElement, ReactNode } from "react";
import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { SidebarClasses } from "./sidebar-theme";

export type NavItemVariant = "item" | "subitem" | "rail";

interface NavItemProps {
  label: string;
  to: string;
  variant: NavItemVariant;
  active: boolean;
  classes: SidebarClasses;
  icon?: LucideIcon;
  trailing?: ReactNode;
  onClick?: () => void;
}

const TOOLTIP_CLASS = cn(
  "rounded-[6px] border-0 bg-tr-navy-dark px-2.5 font-dm py-1.5 text-[12.5px] font-semibold leading-[16px] text-white",
  "shadow-[0_12px_32px_rgba(10,26,51,0.18)]",
);

/** Tooltip do modo recolhido (figma-spec §6, `286:5138`): à direita, hover e foco. */
export function RailTooltip({ label, children }: { label: string; children: ReactElement }) {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8} className={TOOLTIP_CLASS}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

/** Item único do menu: item, subitem e item do rail recolhido (figma-spec §4.4 e §6). */
export function NavItem({ label, to, variant, active, classes, icon: Icon, trailing, onClick }: NavItemProps) {
  const base = cn(
    "relative flex items-center rounded-[8px] outline-none transition-colors focus-visible:ring-2",
    classes.ring,
  );

  if (variant === "rail") {
    return (
      <RailTooltip label={label}>
        <Link
          to={to}
          onClick={onClick}
          aria-label={label}
          aria-current={active ? "page" : undefined}
          className={cn(
            base,
            "h-10 w-12 shrink-0 justify-center",
            active ? classes.active : cn(classes.text, classes.hover),
          )}
        >
          {Icon && <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />}
        </Link>
      </RailTooltip>
    );
  }

  if (variant === "subitem") {
    return (
      <Link
        to={to}
        onClick={onClick}
        aria-current={active ? "page" : undefined}
        className={cn(
          base,
          "h-8 w-full gap-2.5 pl-6 pr-2.5 text-[13.5px] leading-[18px]",
          active ? classes.activeText : cn("font-medium", classes.text, classes.hover),
        )}
      >
        <span className={cn("h-[5px] w-[5px] shrink-0 rounded-full", active ? "bg-current" : classes.dot)} />
        <span className="flex-1 truncate">{label}</span>
      </Link>
    );
  }

  return (
    <Link
      to={to}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={cn(
        base,
        "h-9 w-full gap-2.5 px-2.5 text-[13.5px] leading-[18px]",
        active ? classes.active : cn("font-medium", classes.text, classes.hover),
      )}
    >
      {active && (
        <span
          aria-hidden="true"
          className={cn("absolute left-0 top-[9px] h-[18px] w-[3px] rounded-[2px]", classes.activeBar)}
        />
      )}
      {Icon && <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} aria-hidden="true" />}
      <span className="flex-1 truncate">{label}</span>
      {trailing}
    </Link>
  );
}
